import { useEffect,useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { format } from "date-fns";

import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";

import styles from "./ChooseFileAttachment.module.css";

const TooltipWrapper = ({ children, tooltip, disabled }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);

  const handleMouseEnter = () => {
    if (tooltip && disabled) {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.top - 8,
          left: rect.left + rect.width / 2,
        });
        setShowTooltip(true);
      }
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {showTooltip &&
        tooltip &&
        createPortal(
          <div
            className={styles.customTooltip}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            {tooltip}
            <div className={styles.tooltipArrow}></div>
          </div>,
          document.body
        )}
    </>
  );
};

const FileNameWithTooltip = ({ fileName }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const wrapperRef = useRef(null);
  const textRef = useRef(null);

  const checkTruncation = () => {
    if (textRef.current) {
      const isTruncated =
        textRef.current.scrollWidth > textRef.current.clientWidth;
      setIsTextTruncated(isTruncated);
    }
  };

  useEffect(() => {
    checkTruncation();

    const handleResize = () => {
      checkTruncation();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [fileName]);

  const handleMouseEnter = () => {
    if (isTextTruncated && fileName) {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (rect) {
        setPosition({
          top: rect.top - 8,
          left: rect.left + rect.width / 2,
        });
        setShowTooltip(true);
      }
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={styles.fileNameWrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span ref={textRef} className={styles.fileName}>
          {fileName || "-"}
        </span>
      </div>
      {showTooltip &&
        isTextTruncated &&
        fileName &&
        createPortal(
          <div
            className={styles.customTooltip}
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
          >
            {fileName}
            <div className={styles.tooltipArrow}></div>
          </div>,
          document.body
        )}
    </>
  );
};

const StatusBadge = ({ status }) => {
  const getStatusClass = () => {
    if (!status) return styles.statusDefault;
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case "NEW":
        return styles.statusNew;
      case "IN_REVIEW":
        return styles.statusInReview;
      case "REVIEWED":
        return styles.statusReviewed;
      case "REFERRAL":
        return styles.statusReferral;
      default:
        return styles.statusDefault;
    }
  };

  return (
    <span className={`${styles.statusBadge} ${getStatusClass()}`}>
      {status?.toUpperCase() || "-"}
    </span>
  );
};

const getExtension = (item) => {
  if (item?.fileType) {
    return item.fileType.toString().toLowerCase();
  }
  if (item?.fileName?.includes(".")) {
    return item.fileName.split(".").pop().toLowerCase();
  }
  return "";
};

const isImageFile = (item) => {
  const ext = getExtension(item);
  const imageExtensions = [
    "jpg",
    "jpeg",
    "png",
    "gif",
    "bmp",
    "tif",
    "tiff",
    "webp",
    "svg",
  ];
  return imageExtensions.includes(ext);
};

const isFileSizeValid = (item, maxFileSizeMb, maxFileSizeImageMb) => {
  if (!item?.fileSize) return true;

  const fileSizeInBytes = item.fileSize;
  const isImage = isImageFile(item);

  if (isImage && maxFileSizeImageMb != null) {
    const maxSizeInBytes = maxFileSizeImageMb * 1024 * 1024;
    return fileSizeInBytes <= maxSizeInBytes;
  }

  if (!isImage && maxFileSizeMb != null) {
    const maxSizeInBytes = maxFileSizeMb * 1024 * 1024;
    return fileSizeInBytes <= maxSizeInBytes;
  }

  return true;
};

export const ChooseFileAttachment = ({
  items = [],
  selectedIds = [],
  onSelectionChange = () => {},
  maxSelection,
  allowedExtensions = [],
  onDone = () => {},
  maxFileSizeMb,
  maxFileSizeImageMb,
}) => {
  const allowedExtensionsSet = useMemo(() => {
    return new Set(allowedExtensions.filter(Boolean));
  }, [allowedExtensions]);

  const hasItems = items.length > 0;

  const isExtensionAllowed = (item) => {
    if (!allowedExtensionsSet.size) return true;
    const ext = getExtension(item);
    if (!ext) return false;
    return allowedExtensionsSet.has(ext);
  };

  const isFileSizeAllowed = (item) => {
    return isFileSizeValid(item, maxFileSizeMb, maxFileSizeImageMb);
  };

  const hasSelectionLimit = Number.isFinite(maxSelection) && maxSelection > 0;
  const isSelectionLimitReached =
    hasSelectionLimit && selectedIds.length >= maxSelection;

  const handleToggle = (item) => {
    const isSelected = selectedIds.includes(item.id);
    if (isSelected) {
      onSelectionChange(selectedIds.filter((id) => id !== item.id));
      return;
    }

    const limitReached =
      hasSelectionLimit && selectedIds.length >= maxSelection;

    if (limitReached) {
      return;
    }

    onSelectionChange([...selectedIds, item.id]);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const [datePart, timePart] = dateString.split(" ");
      const [day, month, year] = datePart.split("-");
      const date = new Date(
        `${year}-${month}-${day}${timePart ? ` ${timePart}` : ""}`
      );
      return format(date, "dd-MM-yyyy");
    } catch (error) {
      return dateString;
    }
  };

  const formatDocumentType = (docType) => {
    if (!docType) return "-";
    return docType
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "-";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const helperTextParts = [
    allowedExtensionsSet.size
      ? `Allowed: ${[...allowedExtensionsSet]
          .map((ext) => ext.toUpperCase())
          .join(", ")}`
      : null,
    hasSelectionLimit
      ? `Max ${maxSelection} attachment${maxSelection === 1 ? "" : "s"}`
      : null,
    maxFileSizeMb != null ? `Max file size: ${maxFileSizeMb} MB` : null,
    maxFileSizeImageMb != null
      ? `Max image size: ${maxFileSizeImageMb} MB`
      : null,
  ].filter(Boolean);

  return (
    <>
      <div className={styles.selectionInfo}>
        <p>
          Selected {selectedIds.length}
          {hasSelectionLimit ? ` / ${maxSelection}` : ""} attachments
        </p>
        {helperTextParts.length > 0 && (
          <p className={styles.selectionHelper}>
            {helperTextParts.join(" • ")}
          </p>
        )}
      </div>
      <div className={styles.vaccineDetailsWrapper}>
        <div className={styles.tablewrapper}>
          <div className={styles.table}>
            <div className={styles.tableHeader}>
              <p>Select</p>
              <p>File Type</p>
              <p>File Name</p>
              <p>File Size</p>
              <p>Document Type</p>
              <p>Status</p>
              <p>Creation Date</p>
            </div>
            {hasItems ? (
              <ul className={styles.tableItems}>
                {items.map((item) => {
                  const isSelected = selectedIds.includes(item.id);
                  const isAllowed = isExtensionAllowed(item);
                  const isSizeAllowed = isFileSizeAllowed(item);
                  const extensionLabel =
                    item.fileType?.toUpperCase() ||
                    getExtension(item)?.toUpperCase() ||
                    "-";
                  const disableCheckbox =
                    (!isSelected && isSelectionLimitReached) ||
                    !isAllowed ||
                    !isSizeAllowed;

                  let disableReason = null;
                  if (!isAllowed) {
                    disableReason = "File type not allowed";
                  } else if (!isSizeAllowed) {
                    const isImage = isImageFile(item);
                    const maxSize = isImage
                      ? maxFileSizeImageMb
                      : maxFileSizeMb;
                    disableReason = `File size exceeds maximum limit of ${maxSize} MB`;
                  } else if (!isSelected && isSelectionLimitReached) {
                    disableReason = `Maximum ${maxSelection} attachment${
                      maxSelection === 1 ? "" : "s"
                    } already selected`;
                  }

                  return (
                    <li
                      key={item.id}
                      className={`${styles.tableItem} ${
                        !isAllowed || !isSizeAllowed
                          ? styles.tableItemDisabled
                          : ""
                      }`}
                    >
                      <TooltipWrapper
                        tooltip={disableCheckbox ? disableReason : null}
                        disabled={disableCheckbox}
                      >
                        <div className={styles.checkboxWrapper}>
                          <CustomCheckbox
                            checked={isSelected}
                            disabled={disableCheckbox}
                            aria-label={`Select ${item.fileName || "document"}`}
                            handleChange={() => handleToggle(item)}
                          />
                        </div>
                      </TooltipWrapper>
                      <p
                        className={`${styles.fileTypeCell} ${
                          !isAllowed ? styles.fileTypeError : ""
                        }`}
                      >
                        {extensionLabel}
                      </p>
                      <FileNameWithTooltip fileName={item.fileName} />
                      <p className={!isSizeAllowed ? styles.fileSizeError : ""}>
                        {formatFileSize(item.fileSize)}
                      </p>
                      <p>{formatDocumentType(item.documentType)}</p>
                      <div>
                        <StatusBadge status={item.status} />
                      </div>
                      <p>{formatDate(item.created)}</p>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className={styles.emptyState}>
                <p className={styles.emptyStateTitle}>No documents available</p>
                <p className={styles.emptyStateText}>
                  When documents are available they will appear in this list.
                </p>
              </div>
            )}
          </div>
          <div className={styles.tableFooter}>
            <button
              type="button"
              className={styles.doneButton}
              onClick={onDone}
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
